import React, { useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet';
import { init, send } from 'emailjs-com';
import Navbar from './Navbar';
import Footer from './Footer';
import { TbBuildingWarehouse, TbBatteryCharging, TbTrendingDown, TbLeaf, TbChartLine, TbPlug, TbMapPin, TbBulb } from 'react-icons/tb';

init("d1zH6UNPIGfFtdCEu");

function WarehousesSolar() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    facilityType: '',
    message: ''
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Update document title and meta tags for SEO
    document.title = "Solar for Warehouses | Logistics Park Solar Solutions | Lunar Electric";

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Solar-powered warehouses that cut your energy cost per square foot and stabilize cost per order. Rooftop solar engineered for large-span warehouse roofs and logistics parks.');
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'solar for warehouses, logistics park solar, warehouse solar installation, Grade-A warehouse solar, e-commerce fulfilment solar, 3PL solar, distribution centre solar, warehouse energy efficiency, kWh per m²');
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Solar for Warehouses | Logistics Park Solar Solutions | Lunar Electric');
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Solar-powered warehouses that cut your energy cost per square foot and stabilize cost per order.');
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://lunarelectric.in/solar-warehouses');
    }

    // Update canonical URL
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', 'https://lunarelectric.in/solar-warehouses');
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const templateParams = {
      to_name: 'Lunar Electric',
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone,
      company_name: formData.companyName,
      facility_type: formData.facilityType,
      message: `Warehouse Solar Inquiry: ${formData.message}`,
      subject: 'Warehouse Solar Inquiry'
    };

    send('service_8x7kqhj', 'template_warehouse', templateParams)
      .then((response) => {
        alert('Thank you! We will contact you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          companyName: '',
          facilityType: '',
          message: ''
        });
      })
      .catch((error) => {
        alert('Failed to send inquiry, please try again.');
        console.error('EmailJS error:', error);
      });
  };

  return (
    <>
      <Helmet>
        <title>Solar for Warehouses | Logistics Park Solar Solutions | Lunar Electric</title>
        <meta name="description" content="Solar-powered warehouses that cut your energy cost per square foot and stabilize cost per order. Rooftop solar engineered for large-span warehouse roofs and logistics parks." />
        <meta name="keywords" content="solar for warehouses, logistics park solar, warehouse solar installation, Grade-A warehouse solar, e-commerce fulfilment solar, 3PL solar, distribution centre solar, warehouse energy efficiency" />
        <link rel="canonical" href="https://lunarelectric.in/solar-warehouses" />
      </Helmet>
      <Navbar />
      <div className="warehouses-page">
        {/* Hero Section */}
        <section className="warehouses-hero">
          <div className="container">
            <div className="row">
              <div className="col-md-12 text-center">
                <h1 className="hero-title">
                  Solar-Powered Warehouses That Cut Your Energy Cost Per Square Foot
                </h1>
                <p className="hero-subtitle">
                  Reduce kWh/m² and stabilise cost per order with rooftop solar engineered for large-span warehouse roofs
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why We Specialize Section */}
        <section className="specialization-section">
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <h2 className="section-title">Why We Specialize in Warehouse & Logistics Solar Solutions</h2>
                <p className="section-intro">
                  We design solar solutions specifically for Grade-A warehouses, logistics parks, fulfilment centres, CFS (Container Freight Stations), 
                  and ICDs (Inland Container Depots). Our warehousing expertise means we understand your critical metrics—kWh/m², energy cost per square foot, 
                  and cost per order requirements.
                </p>
                
                <div className="specialization-grid">
                  <div className="specialization-card">
                    <TbBuildingWarehouse className="specialization-icon" />
                    <h3>Logistics Park Expertise</h3>
                    <p>We understand Grade-A warehouses, modern logistics parks, built-to-suit facilities, CFS (Container Freight Stations), ICDs (Inland Container Depots), multi-client distribution centres, and e-commerce fulfilment centres. Our designs account for high-bay racked storage, clear height, dock doors, and staging areas.</p>
                  </div>
                  <div className="specialization-card">
                    <TbChartLine className="specialization-icon" />
                    <h3>Load-Profile Engineering</h3>
                    <p>Plants are engineered around your lighting, HVAC, and MHE load profile, not generic thumb rules. Our design considers cross-docking and sortation zones, MHE and automation loads, and office block requirements.</p>
                  </div>
                  <div className="specialization-card">
                    <TbBulb className="specialization-icon" />
                    <h3>Lighting & HVAC Optimization</h3>
                    <p>We focus on reducing the share of lighting and comfort HVAC in your total energy consumption, which can easily account for the majority of non-refrigerated warehouse electricity use. Energy-efficient lighting (LED high-bays) and HVAC for office blocks.</p>
                  </div>
                  <div className="specialization-card">
                    <TbLeaf className="specialization-icon" />
                    <h3>Green Certification Support</h3>
                    <p>We ensure your solar solution supports energy-efficiency and green-building goals under emerging green logistics park and energy-benchmarking frameworks, including IGBC Green Logistics Parks & Warehouses certification.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Metrics That Matter Section */}
        <section className="metrics-section">
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <h2 className="section-title">Metrics That Matter to Your Operations</h2>
                <p className="section-intro">
                  We track and optimize the KPIs that directly impact your warehouse operations and profitability. 
                  Our solutions are designed to improve these critical metrics:
                </p>
                
                <div className="metrics-grid">
                  <div className="metric-card">
                    <div className="metric-header">
                      <TbTrendingDown className="metric-icon" />
                      <h3>Energy Cost Per Square Foot</h3>
                    </div>
                    <p className="metric-description">
                      Designed to reduce energy cost per square foot per month by optimising the grid + solar mix for your actual operating schedule. 
                      This internal KPI helps assess whether your park or warehouse is energy efficient or wasting on lighting/HVAC.
                    </p>
                    <div className="metric-impact">
                      <strong>Impact:</strong> Lower monthly energy costs and improve warehouse energy benchmarking
                    </div>
                  </div>

                  <div className="metric-card">
                    <div className="metric-header">
                      <TbChartLine className="metric-icon" />
                      <h3>Energy Use Intensity (EUI)</h3>
                    </div>
                    <p className="metric-description">
                      Aims to lower kWh/m² per year (or kWh per square foot per year), often used in green warehouse and IGBC/LEED benchmarking. 
                      Improve your warehouse energy performance without disrupting operations or throughput.
                    </p>
                    <div className="metric-impact">
                      <strong>Impact:</strong> Align with green logistics park certification targeting 20–30% energy savings
                    </div>
                  </div>

                  <div className="metric-card">
                    <div className="metric-header">
                      <TbBuildingWarehouse className="metric-icon" />
                      <h3>Energy Cost Per Order/Pallet</h3>
                    </div>
                    <p className="metric-description">
                      Supports more stable cost per order and cost per pallet by de-risking 15–20% of your power cost from tariff hikes and outages. 
                      Links directly to cost-to-serve and margin per order in 3PL and e-commerce fulfilment environments.
                    </p>
                    <div className="metric-impact">
                      <strong>Impact:</strong> Improve profitability per order and reduce energy overheads
                    </div>
                  </div>

                  <div className="metric-card">
                    <div className="metric-header">
                      <TbBatteryCharging className="metric-icon" />
                      <h3>Grid vs Diesel Split</h3>
                    </div>
                    <p className="metric-description">
                      Track units from grid vs units from DG and diesel generator run-hours per month, especially for parks in low-reliability grids. 
                      Helps cut diesel generator run-hours per month while maintaining 24×7 uptime for loading bays, automation, and office blocks.
                    </p>
                    <div className="metric-impact">
                      <strong>Impact:</strong> Reduce operational costs and improve reliability
                    </div>
                  </div>

                  <div className="metric-card">
                    <div className="metric-header">
                      <TbPlug className="metric-icon" />
                      <h3>Contract Demand & Peak kVA</h3>
                    </div>
                    <p className="metric-description">
                      Demand charges and penalties matter significantly for large, automated warehouses with high MHE and conveyor/sortation loads. 
                      Our solutions help optimize contract demand and reduce peak kVA charges.
                    </p>
                    <div className="metric-impact">
                      <strong>Impact:</strong> Lower demand charges and penalties, improving overall power cost structure
                    </div>
                  </div>

                  <div className="metric-card">
                    <div className="metric-header">
                      <TbLeaf className="metric-icon" />
                      <h3>Solar Share of Annual Units</h3>
                    </div>
                    <p className="metric-description">
                      Designed to achieve significant solar share of annual units across your logistics park portfolio using idle roof space. 
                      Maximize solar contribution during peak tariff periods and outage-prone hours.
                    </p>
                    <div className="metric-impact">
                      <strong>Impact:</strong> Increase renewable energy share while improving ESG scores for logistics assets
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Value Propositions */}
        <section className="value-props-section">
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <h2 className="section-title">How We Deliver Results</h2>
                <div className="value-props-grid">
                  <div className="value-prop-card">
                    <h3>Load-Curve Based Design</h3>
                    <p>Plants are engineered around your lighting, HVAC, and MHE load profile, not generic thumb rules. Our design considers your high-bay racked storage, dock doors, staging areas, cross-docking and sortation zones, and automation loads.</p>
                  </div>
                  <div className="value-prop-card">
                    <h3>Peak Tariff & DG Relief</h3>
                    <p>Targets maximum solar contribution during peak tariff slabs and outage-prone hours to cut DG usage. This helps optimize contract demand and reduce demand charges for large, automated warehouses with high MHE and conveyor/sortation loads.</p>
                  </div>
                  <div className="value-prop-card">
                    <h3>Multi-Site Ready</h3>
                    <p>Pan-India deployment for logistics parks, warehouse networks, CFS, and ICD facilities, with central monitoring of plant kWh, diesel hours, and site-wise energy KPIs. Ideal for portfolio-level energy management.</p>
                  </div>
                  <div className="value-prop-card">
                    <h3>Large-Span Rooftop Solutions</h3>
                    <p>Grid + rooftop solar + DG integrated architecture with large-span rooftop solar for logistics sheds. Solar-ready expansion for new blocks without disrupting operations or throughput.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Industry Benefits */}
        <section className="benefits-section">
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <h2 className="section-title">Benefits for Your Warehouse Operations</h2>
                <div className="benefits-list">
                  <div className="benefit-item">
                    <h4>Reduce Lighting & HVAC Share</h4>
                    <p>Lower the share of lighting and comfort HVAC in your total energy consumption, which can easily account for the majority of non-refrigerated warehouse electricity use. Energy-efficient lighting (LED high-bays) and daylight harvesting.</p>
                  </div>
                  <div className="benefit-item">
                    <h4>Improve Energy Use Intensity</h4>
                    <p>Lower your kWh/m² per year and improve warehouse energy performance, aligning with green logistics park certification and energy-benchmarking frameworks. Resource-efficient warehousing.</p>
                  </div>
                  <div className="benefit-item">
                    <h4>Decarbonise Warehousing Operations</h4>
                    <p>Improve ESG scores for logistics assets while reducing energy overheads per order, supporting your sustainability and resource-efficient warehousing goals.</p>
                  </div>
                  <div className="benefit-item">
                    <h4>Stabilise Cost Per Order</h4>
                    <p>De-risk power costs from tariff hikes and outages, helping stabilise cost per order and cost per pallet, improving overall profitability and cost-to-serve metrics.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="contact-form-section">
          <div className="container">
            <div className="row">
              <div className="col-lg-5 col-md-6">
                <div className="form-side-content">
                  <h3>Why Choose Lunar Electric?</h3>
                  <div className="stats-grid">
                    <div className="stat-item">
                      <div className="stat-number">15-20%</div>
                      <div className="stat-label">Power Cost Reduction</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number">24×7</div>
                      <div className="stat-label">Operational Uptime</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number">Pan-India</div>
                      <div className="stat-label">Deployment Network</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-number">IGBC</div>
                      <div className="stat-label">Green Certified</div>
                    </div>
                  </div>
                  <div className="key-benefits">
                    <h4>Key Benefits:</h4>
                    <ul>
                      <li>Reduce energy cost per sq ft</li>
                      <li>Lower kWh/m² consumption</li>
                      <li>Stabilize cost per order</li>
                      <li>Cut diesel generator hours</li>
                      <li>Optimize contract demand</li>
                      <li>Support CFS & ICD facilities</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-6 col-md-6 offset-lg-1">
                <div className="form-wrapper">
                  <h2 className="form-title">Get a Customized Solar Solution</h2>
                  <p className="form-subtitle">
                    Tell us about your facility and we'll design a solution that optimizes your energy cost per square foot.
                  </p>
                  
                  <form onSubmit={handleSubmit} className="warehouses-form">
                    <div className="form-group">
                      <label htmlFor="name">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="companyName">Company/Park Name *</label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="facilityType">Facility Type *</label>
                      <select
                        id="facilityType"
                        name="facilityType"
                        value={formData.facilityType}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      >
                        <option value="">Select Facility Type</option>
                        <option value="Grade-A Warehouse">Grade-A Warehouse</option>
                        <option value="Modern Logistics Park">Modern Logistics Park</option>
                        <option value="CFS (Container Freight Station)">CFS (Container Freight Station)</option>
                        <option value="ICD (Inland Container Depot)">ICD (Inland Container Depot)</option>
                        <option value="Built-to-Suit Facility">Built-to-Suit Facility</option>
                        <option value="Multi-Client Distribution Centre">Multi-Client Distribution Centre</option>
                        <option value="E-commerce Fulfilment Centre">E-commerce Fulfilment Centre</option>
                        <option value="3PL Warehouse">3PL Warehouse</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="message">Tell us about your facility (size, operations, current energy costs, etc.)</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        className="form-input"
                        rows="5"
                        placeholder="e.g., Total area in sq ft/m², number of dock doors, automation level, current kWh/m², diesel hours per month, contract demand..."
                      />
                    </div>

                    <div className="form-submit">
                      <button type="submit" className="btn btn-primary">
                        Get Free Consultation
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="container">
            <div className="row">
              <div className="col-md-12 text-center">
                <h2>Ready to Optimize Your Warehouse Energy Costs?</h2>
                <p>De-risk your warehousing network from grid failures and diesel price shocks with tailored solar for logistics and fulfilment centres</p>
                <Link to="/" className="btn btn-secondary">
                  ← Back to Home
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}

export default WarehousesSolar;
